import { FormControl, Stack, SxProps } from "@mui/material";
import { DatePicker, DatePickerProps, PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { SuggestedChip, SuggestedChipProps } from "components/ArtificialIntelligence/SuggestedChip";
import { SuggestedIcon } from "components/ArtificialIntelligence/SuggestedIcon";
import { DateShortcutChips } from "components/DateShortcutChips";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { Controller, FieldValues, UseControllerProps } from "react-hook-form";

import { DisplayOnlyField } from "./DisplayOnlyField";
import { ErrorDisplay } from "./ErrorDisplay";
import { InputTooltip } from "./InputTooltip";

type Props<T extends FieldValues> = {
  label: string;
  tooltip?: React.ReactNode;
  datePickerProps?: DatePickerProps<DateTime>;
  sx?: SxProps;
  disabled?: boolean;
  required?: boolean;
  hideErrors?: boolean;
  helperText?: string;
  endAdornment?: React.ReactNode;
  shortcuts?: PickersShortcutsItem<DateTime>[]; // optional date shortcuts rendered inside the picker
  clearable?: boolean; // whether the field can be cleared (defaults to !required)
  suggestionProps?: SuggestedChipProps & { showFullSuggestionChip?: boolean };
} & UseControllerProps<T>;

export function DateField<T extends FieldValues>(props: Props<T>) {
  // 72px accounts for tooltip (24px) + calendar icon (24px) + padding/spacing
  // 48px accounts for just calendar icon (24px) + padding/spacing
  const slotWidth = props.tooltip ? 72 : 48;
  const clearable = props.clearable ?? !props.required;

  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const value = formContext?.getValues(props.name) as string | undefined;
    const displayValue = value ? DateTime.fromISO(value).toFormat("MM/dd/yyyy") : "";
    return <DisplayOnlyField text={displayValue} label={props.label} />;
  }

  return (
    <FormControl fullWidth>
      <Controller
        name={props.name}
        control={props.control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <>
              <DatePicker
                sx={props.sx}
                {...props.datePickerProps}
                label={props.label}
                value={value ? DateTime.fromISO(value) : null}
                onChange={(newValue) => onChange(newValue?.toISODate() ?? null)}
                disabled={props.disabled}
                slots={{
                  inputAdornment: (adornmentProps) => (
                    <Stack direction="row" alignItems="center">
                      {props.tooltip && <InputTooltip title={props.tooltip} placement="right" />}
                      {props.suggestionProps && props.suggestionProps.showFullSuggestionChip ? (
                        <SuggestedChip {...props.suggestionProps} />
                      ) : (
                        <SuggestedIcon {...props.suggestionProps} />
                      )}
                      {props.endAdornment}
                      {adornmentProps.children}
                    </Stack>
                  ),
                  ...(props.shortcuts && props.shortcuts.length > 0
                    ? { shortcuts: DateShortcutChips }
                    : {}),
                }}
                slotProps={{
                  inputAdornment: {
                    ...props.datePickerProps?.slotProps?.inputAdornment,
                    position: props.suggestionProps ? "start" : undefined,
                  },
                  ...props.datePickerProps?.slotProps,
                  ...(clearable ? { field: { clearable: true } } : {}),
                  textField: {
                    ...props.datePickerProps?.slotProps?.textField,
                    error: !!error,
                    required: props.required,
                    sx: {
                      "& .MuiInputLabel-shrink": {
                        width: "auto", // Full width when shrunk/floating
                      },
                      "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                        width: `calc(100% - ${slotWidth}px)`, // Limit width to avoid overlap with tooltip/date picker icons
                      },
                      ...(props.datePickerProps?.slotProps?.textField &&
                      "sx" in props.datePickerProps.slotProps.textField
                        ? props.datePickerProps.slotProps.textField.sx
                        : {}),
                    },
                  },
                  actionBar: {
                    ...props.datePickerProps?.slotProps?.actionBar,
                    actions: clearable ? ["clear", "accept"] : ["accept"],
                  },
                  ...(props.shortcuts && props.shortcuts.length > 0
                    ? {
                        shortcuts: {
                          items: props.shortcuts,
                        },
                      }
                    : {}),
                }}
              />
              <ErrorDisplay
                error={error}
                helperText={props.helperText}
                hideErrors={props.hideErrors}
              />
            </>
          );
        }}
      />
    </FormControl>
  );
}
